/**
 * ContractorOutreachDialog — Evidence Assist EA6.
 *
 * For each unsatisfied checklist row, surfaces a draft email + a curated
 * list of typical regional providers so the user can fire off a real
 * scoping request without leaving the platform.
 *
 * The dialog reads:
 *   - the row's docTypeLabel + checklistRowId (props)
 *   - the active SED's site context (siteName, county, state, acres, etc.)
 *   - the curated provider list from `src/data/regionalContractors.json`
 *
 * The draft email is composed from a template tied to `checklistRowId`,
 * with site context interpolated in. Copy / open-in-mail-client buttons
 * complete the loop. The contractor list is filtered by the site's state
 * (when known) and falls back to the national / general list otherwise.
 */
import React, { useMemo, useState } from 'react';
import { X, Copy, ExternalLink, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import contractorsData from '../../data/regionalContractors.json';
import type { SiteEntity } from '../../types/site';

export interface SiteContextForOutreach {
  siteName: string;
  address: string;
  county?: string;
  state?: string;
  acres?: number;
  projectType?: string;
}

interface ContractorEntry {
  name: string;
  region: string;             // state code or 'national'
  specialty: string[];        // checklistRowIds it covers
  contact: string;
  notes: string;
  costBand?: string;
  timelineBand?: string;
  url?: string;
}

/**
 * The document-type label the dialog titles itself with, derived from an
 * evidence step's label ("Add evidence: Phase I ESA" → "Phase I ESA"). One
 * helper for every host that opens the dialog from an evidence step (the Site
 * Evidence ledger and Path to GO's Verify phase) so their titles cannot drift.
 */
export function outreachDocTypeLabel(stepLabel: string): string {
  return stepLabel.replace(/^Add evidence: /i, '').replace(/^Upload /i, '');
}

/** The dialog's site context for a site — the same shape for every host. */
export function outreachSiteContext(
  site: Pick<SiteEntity, 'name' | 'address' | 'siteArea' | 'projectIntent'>,
): SiteContextForOutreach {
  return {
    siteName: site.name,
    address: site.address || 'the site',
    acres: site.siteArea,
    projectType: site.projectIntent?.projectType ?? 'data center campus',
  };
}

interface Props {
  checklistRowId: string;
  docTypeLabel: string;
  siteContext: SiteContextForOutreach | null;
  onClose: () => void;
}

/** Per-row email template. `{...}` placeholders are interpolated. Keep
 *  short — the user will edit before sending. */
const EMAIL_TEMPLATES: Record<string, { subject: string; body: string }> = {
  'geotech': {
    subject: 'Geotechnical investigation — {siteName}, {county}, {state}',
    body: `Hi,

We're evaluating {siteName} ({acres} acres at {address}) for a {projectType}. Per our feasibility analysis the site requires a geotechnical investigation (soil borings + foundation recommendations) before final design.

Could you provide a scope + timeline + budget estimate for:
- N borings to 30 m depth
- Site classification per ASCE 7-22
- Foundation recommendations
- A draft geotechnical report

Thanks,`,
  },
  'phase1-esa': {
    subject: 'Phase I ESA — {siteName}, {county}, {state}',
    body: `Hi,

We're evaluating {siteName} ({acres} acres at {address}) for a {projectType}. Per our environmental screening the site requires a Phase I Environmental Site Assessment per ASTM E1527-21 before NEPA review.

Could you provide a scope + timeline + budget estimate for the Phase I, including:
- Records review (federal/state/local environmental databases)
- Site reconnaissance
- Adjacent property review
- Historical use analysis
- Recognized Environmental Conditions (RECs) assessment

Thanks,`,
  },
  'utility-letter': {
    subject: 'Service letter / interconnection scoping — {siteName}, {county}, {state}',
    body: `Hi,

We're evaluating {siteName} ({address}) for a {projectType} requiring substantial electrical load. Could the serving utility issue a service letter or capacity availability statement covering:
- Available substation capacity at the nearest POI
- Anticipated upgrades and timeline
- Indicative interconnection cost band
- Available rate structures

Thanks,`,
  },
  'fiber-survey': {
    subject: 'Fiber survey / carrier quote — {siteName}, {county}, {state}',
    body: `Hi,

We're evaluating {siteName} ({address}) for a {projectType}. We need to understand the fiber options for this site:
- Lit fiber availability + quoted bandwidths
- Dark fiber pull cost / timeline
- Path diversity from at least two carriers
- Connectivity to nearest carrier-neutral data center / IX

Could you provide a fiber survey + carrier quote?

Thanks,`,
  },
  'survey': {
    subject: 'Topographic / boundary survey — {siteName}, {county}, {state}',
    body: `Hi,

We need a stamped topographic + boundary survey for {siteName} ({acres} acres at {address}) in preparation for a {projectType}:
- ALTA-grade boundary
- Topo at 1-ft contours
- Easements + recorded encumbrances

Could you provide a scope + timeline + budget?

Thanks,`,
  },
  'water-rights': {
    subject: 'Water rights review — {siteName}, {county}, {state}',
    body: `Hi,

We're evaluating {siteName} ({address}) in {state} for a {projectType} with material water demand. Could you provide a water-rights review for this parcel — chain of appropriation, current rights, available augmentation paths, and the regulatory pathway for additional appropriations if needed?

Thanks,`,
  },
  'zoning': {
    subject: 'Zoning verification request — {siteName}, {county}, {state}',
    body: `Hi,

We're evaluating {siteName} ({acres} acres at {address}) for a {projectType}. We need to verify the current zoning designation and allowable uses for this parcel.

Could you please provide:
- The current zoning code
- Permitted uses (specifically confirming if a {projectType} is allowed by-right or requires a conditional use permit)
- Required setbacks and height limits
- Any special conditions or overlays affecting the parcel

Thanks,`,
  },
};

// Map parcel-survey to the survey template
EMAIL_TEMPLATES['parcel-survey'] = EMAIL_TEMPLATES['survey'];

function defaultTemplate(docTypeLabel: string): { subject: string; body: string } {
  return {
    subject: `${docTypeLabel} — {siteName}, {county}, {state}`,
    body: `Hi,

We're evaluating {siteName} ({acres} acres at {address}) for a {projectType}. The project requires a {docTypeLabel}.

Could you provide a scope + timeline + budget estimate?

Thanks,`,
  };
}

function interpolate(template: string, ctx: SiteContextForOutreach | null, docTypeLabel: string): string {
  const map: Record<string, string> = {
    '{siteName}':    ctx?.siteName ?? 'the site',
    '{address}':     ctx?.address ?? 'the address',
    '{county}':      ctx?.county ?? '',
    '{state}':       ctx?.state ?? '',
    '{acres}':       ctx?.acres ? `${ctx.acres.toFixed(0)}` : 'N',
    '{projectType}': ctx?.projectType ?? 'data center campus',
    '{docTypeLabel}': docTypeLabel,
  };
  return Object.entries(map).reduce(
    (s, [k, v]) => s.split(k).join(v),
    template,
  );
}

/** Filter the curated list by row specialty + site state. */
function contractorsFor(rowId: string, state: string | undefined): ContractorEntry[] {
  const all = (contractorsData as unknown as { contractors: ContractorEntry[] }).contractors ?? [];
  const matchesRow = (c: ContractorEntry) => c.specialty.includes(rowId);
  const matchesState = (c: ContractorEntry) =>
    !state || c.region === state || c.region === 'national';
  return all.filter(c => matchesRow(c) && matchesState(c)).slice(0, 8);
}

export const ContractorOutreachDialog: React.FC<Props> = ({
  checklistRowId, docTypeLabel, siteContext, onClose,
}) => {
  const tmpl = EMAIL_TEMPLATES[checklistRowId] ?? defaultTemplate(docTypeLabel);
  const subject = useMemo(
    () => interpolate(tmpl.subject, siteContext, docTypeLabel),
    [tmpl.subject, siteContext, docTypeLabel],
  );
  const body = useMemo(
    () => interpolate(tmpl.body, siteContext, docTypeLabel),
    [tmpl.body, siteContext, docTypeLabel],
  );
  const contractors = useMemo(
    () => contractorsFor(checklistRowId, siteContext?.state),
    [checklistRowId, siteContext?.state],
  );

  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const full = `Subject: ${subject}\n\n${body}`;
    try {
      await navigator.clipboard.writeText(full);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* clipboard may be denied — silent */ }
  };

  const handleMailto = () => {
    const url = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="contractor-outreach-title"
      data-testid="contractor-outreach-dialog"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border w-full max-w-2xl max-h-[80vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h2
            id="contractor-outreach-title"
            className="text-[11px] font-bold uppercase tracking-widest"
          >
            Request {docTypeLabel} from contractor
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-text-muted hover:text-text-primary"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="px-4 py-3 space-y-3 text-[10px] text-text-primary">
          {contractors.length > 0 && (
            <div className="space-y-1">
              <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
                Regional providers
              </div>
              <ul className="space-y-1" data-testid="contractor-list">
                {contractors.map((c, idx) => (
                  <li
                    key={`${c.name}-${idx}`}
                    className="flex items-start gap-2 border border-border px-2 py-1.5"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-text-primary">
                        {c.name}{c.region && c.region !== 'national' ? ` · ${c.region}` : ''}
                      </div>
                      <div className="type-body text-text-muted">{c.notes}</div>
                      {(c.costBand || c.timelineBand) && (
                        <div className="type-body text-text-secondary">
                          {c.costBand}{c.costBand && c.timelineBand ? ' · ' : ''}{c.timelineBand}
                        </div>
                      )}
                    </div>
                    {c.url && (
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:text-primary/80 shrink-0"
                        aria-label={`Open ${c.name} in a new tab`}
                      >
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="space-y-1">
            <div className="type-body font-bold uppercase tracking-widest text-text-secondary">
              Draft email
            </div>
            <div className="border border-border bg-state-hover p-2 space-y-2">
              <div>
                <span className="text-text-muted">Subject: </span>
                <span className="font-semibold">{subject}</span>
              </div>
              <pre
                className="whitespace-pre-wrap font-[inherit] text-[10px]"
                data-testid="contractor-outreach-body"
              >{body}</pre>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-border">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCopy}
            data-testid="contractor-outreach-copy"
          >
            <Copy className="w-3 h-3 mr-1" /> {copied ? 'Copied' : 'Copy email'}
          </Button>
          <Button
            size="sm"
            onClick={handleMailto}
            data-testid="contractor-outreach-mailto"
          >
            <Mail className="w-3 h-3 mr-1" /> Open in mail client
          </Button>
        </div>
      </div>
    </div>
  );
};
